import React, { useState, useEffect } from "react";
import { FaPlus, FaTrash, FaImage, FaEdit } from "react-icons/fa";
import { API } from "../../Services/axios";
import { URLS } from "../../Services/Urls";
import { toast } from "react-toastify";
const ContentManager = ({ section, onContentAdd, onContentRemove, thisContent, onContentUpdate }) => {
  // --- SIMPLIFICATION : L'ordre est maintenant géré par existingContentCount ---

  useEffect(() => {
    if (thisContent?.id) {
      // Pré-remplir le formulaire d'édition et ouvrir la modal
      (async () => {
        try {
          const resp = await API.get(`/be/admin/contents/${thisContent.id}/`);
          const data = resp.data;
          // Map data to our newContent shape as best as possible
          setNewContent(prev => ({
            ...prev,
            type: data.content_type || data.type || prev.type,
            name: data.title || data.name || prev.name,
            description: data.description || data.text || prev.description,
            imagePreview: data.image || prev.imagePreview,
            label: data.label || prev.label,
            placeholder: data.placeholder || prev.placeholder,
            suffix: data.suffix || prev.suffix,
            associatedInputs: data.inputs || data.associatedInputs || prev.associatedInputs
          }));
          setShowModal(true);
        } catch (err) {
          // si erreur, on laisse la modal fermée
          console.warn('Impossible de charger le contenu pour édition', err);
        }
      })();
    }
  }, [thisContent]); // Dépendance simplifiée

  const [showModal, setShowModal] = useState(false);
  const [newContent, setNewContent] = useState({
    type: "",

    // Product fields
    name: "",
    description: "",
    image: null,
    imagePreview: null,
    // OtherChoice fields
    label: "",

    // Input fields
    placeholder: "",
    suffix: "",
    // Pour les "Autres Choix" avec plusieurs inputs
    associatedInputs: [{ label: "", placeholder: "", suffix: "", input_type: "TEXT" }],
  });

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setNewContent({
        ...newContent,
        image: file,
        imagePreview: URL.createObjectURL(file)
      });
    }
  };

  // --- NOUVELLES FONCTIONS POUR GÉRER LES INPUTS ASSOCIÉS ---

  const handleAssociatedInputChange = (index, field, value) => {
    const updatedInputs = [...newContent.associatedInputs];
    updatedInputs[index][field] = value;
    setNewContent({ ...newContent, associatedInputs: updatedInputs });
  };

  const addAssociatedInput = () => {
    if (newContent.associatedInputs.length < 2) {
      setNewContent({
        ...newContent,
        associatedInputs: [
          ...newContent.associatedInputs,
          { label: "", placeholder: "", suffix: "", input_type: "TEXT" },
        ],
      });
    }
  };

  const removeAssociatedInput = (index) => {
    const updatedInputs = newContent.associatedInputs.filter((_, i) => i !== index);
    setNewContent({ ...newContent, associatedInputs: updatedInputs });
  };
  const handleCreateContent = async () => {
    // Validation selon le type
    if (!newContent.type) {
      toast.error("Veuillez sélectionner un type de contenu");
      return;
    }

    // TODO: Ajouter un état de chargement (ex: setLoading(true))

    try {
      // --- CORRECTION ---
      // On récupère le nombre de contenus actuels pour garantir un ordre unique.
      const sectionDetails = await API.get(`/be/admin/sections/${section.id}/`);
      const existingContentCount = sectionDetails.data.contents.length;

      // 1. Créer le contenu générique
      const contentResponse = await API.post(URLS.BE.CONTENTS.create, {
        order: existingContentCount + 1,
        content_type: newContent.type,
        section: section.id,
        title: newContent.name || newContent.label || `Contenu ${newContent.type}`
      });

      const contentId = contentResponse.data.id;
      // let contentDataForUI = { ...newContent, id: contentId, selected: false };
      // --- CORRECTION ---
      // On utilise la réponse de l'API comme base, qui contient le bon order et id.
      // On y ajoute les détails spécifiques au type qui ne sont pas dans la réponse générique.
      let contentDataForUI = { 
        ...contentResponse.data, // Base de données depuis le backend (id, order, content_type, etc.)
        ...newContent, // Ajoute les détails de l'UI (label, placeholder, imagePreview, etc.)
        id: contentId // Assure que l'ID est bien celui retourné par l'API
      };

      // 2. Créer les données spécifiques au type
      switch (newContent.type) {
        case "PRODUCTS":
          if (!newContent.name ) {
            throw new Error("Veuillez remplir le nom du produit");
          }
          const productFormData = new FormData();
          productFormData.append('name', newContent.name);
          productFormData.append('description', newContent.description);
          productFormData.append('content', contentId);
          if (newContent.image) {
            productFormData.append('image', newContent.image);
          }
          const productResponse = await API.post('/be/admin/products/', productFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          contentDataForUI.image = productResponse.data.image || newContent.imagePreview;
          break;

        case "OTHER_CHOICES":
          if (!newContent.label) {
            throw new Error("Veuillez remplir le label du choix");
          }

          const inputIds = [];
          for (const inputData of newContent.associatedInputs) {
            if (!inputData.label) {
              throw new Error("Veuillez remplir le label de chaque champ de saisie.");
            }
            const inputResponse = await API.post('/be/admin/inputs/', {
              order: (section.content || []).length + 1 + inputIds.length,
              label: inputData.label,
              placeholder: inputData.placeholder,
              suffix: inputData.suffix,
              input_type: inputData.input_type || "TEXT",
              content: contentId,
              section: section.id,
            });
            inputIds.push(inputResponse.data.id);
          }

          // Remplacer par le bon endpoint si différent
          await API.post('/be/admin/other-choices/', {
            order: existingContentCount + 1,
            label: newContent.label,
            content: contentId,
            input: inputIds, // Envoyer le tableau d'IDs
          });
          break;

        case "INPUT":
          if (!newContent.label) {
            throw new Error("Veuillez remplir le label de la saisie");
          }
          await API.post('/be/admin/inputs/', {
            order: existingContentCount + 1,
            label: newContent.label,
            placeholder: newContent.placeholder,
            suffix: newContent.suffix,
            input_type: newContent.input_type || "TEXT",
            content: contentId,
            section: section.id,
          });
          break;

        case "TEXT":
          if (!newContent.name || !newContent.description) {
            throw new Error("Veuillez remplir le titre et la description du texte");
          }
          
          await API.post('/be/admin/TEXT/', {
            // title: newContent.name,
            // description: newContent.description,
            text: newContent.description,
            content: contentId,
          });
          break;

        case "CHOICES":
          if (!newContent.label) {
            throw new Error("Veuillez remplir le label du choix");
          }
          
          await API.post('/be/admin/choices/', {
            order: existingContentCount + 1,
            name: newContent.label,
            content: contentId,
          });
          break;

        default:
          throw new Error("Type de contenu non reconnu");
      }

      // 3. Mettre à jour l'UI si tout s'est bien passé
      onContentAdd(section.id, contentDataForUI);
      toast.success("Contenu ajouté avec succès !");
      
      // Réinitialiser le formulaire
      setNewContent({
        type: "",
        name: "",
        description: "",
        image: null,
        imagePreview: null,
        label: "",
        placeholder: "",
        suffix: "",
        associatedInputs: [{ label: "", placeholder: "", suffix: "", input_type: "TEXT" }],
      });
      setShowModal(false);

    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || "Une erreur est survenue.";
      toast.error(errorMessage);
      console.error("Erreur lors de la création du contenu:", error);
    } // finally { setLoading(false); }

  };

  const handleUpdateContent = async () => {
    if (!thisContent || !thisContent.id) return;

    try {
      // Mettre à jour le contenu générique (title)
      await API.patch(`/be/admin/contents/${thisContent.id}/`, {
        title: newContent.name || newContent.label || undefined,
      });

      // Selon le type, appeler le endpoint de mise à jour correspondant
      switch (newContent.type) {
        case 'PRODUCTS':
          // trouver l'id du product lié : thisContent.product || thisContent.products?.[0]
          if (thisContent.product || thisContent.products?.length) {
            const productId = thisContent.product || thisContent.products?.[0]?.id;
            const fd = new FormData();
            fd.append('name', newContent.name);
            fd.append('description', newContent.description);
            if (newContent.image) fd.append('image', newContent.image);
            await API.patch(`/be/admin/products/${productId}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          }
          break;
        case 'INPUT':
          if (thisContent.input || thisContent.inputs?.length) {
            const inputId = thisContent.input || thisContent.inputs?.[0]?.id;
            await API.patch(`/be/admin/inputs/${inputId}/`, {
              label: newContent.label,
              placeholder: newContent.placeholder,
              suffix: newContent.suffix,
              input_type: newContent.input_type || 'TEXT'
            });
          }
          break;
        case 'TEXT':
          if (thisContent.text || thisContent.TEXT || thisContent.text_id) {
            const textId = thisContent.text || thisContent.TEXT || thisContent.text_id;
            await API.patch(`/be/admin/TEXT/${textId}/`, { text: newContent.description });
          }
          break;
        case 'CHOICES':
          if (thisContent.choices || thisContent.choice) {
            const choiceId = thisContent.choice || thisContent.choices?.[0]?.id;
            await API.patch(`/be/admin/choices/${choiceId}/`, { name: newContent.label });
          }
          break;
        case 'OTHER_CHOICES':
          // TODO: update associated inputs and other-choices record
          break;
        default:
          break;
      }

      // Retourner un objet mis à jour au parent
      const updatedContentForUI = { ...thisContent, ...newContent, id: thisContent.id };
      if (onContentUpdate) onContentUpdate(updatedContentForUI);
      setShowModal(false);
    } catch (err) {
      console.error('Erreur lors de la mise à jour du content', err);
      toast.error('Erreur lors de la mise à jour du contenu');
    }
  };

  const renderContentPreview = () => {
    switch (newContent.type) {
      case "PRODUCTS":
        return (
          <div className="text-center">
            <div className="materiau-wrapper">
              {newContent.imagePreview ? (
                <img
                  src={newContent.imagePreview}
                  alt="Aperçu"
                  style={{ maxWidth: "100px", height: "auto" }}
                />
              ) : (
                <div className="bg-secondary text-white d-flex align-items-center justify-content-center"
                     style={{ width: "100px", height: "100px" }}>
                  <FaImage size={30} />
                </div>
              )}
              <div className="rectangle px-4 py-1 mt-2">
                <p className="Card-title">{newContent.name || "Nom du produit"}</p>
                {newContent.description && (
                  <p className="Card-sub-title">{newContent.description}</p>
                )}
              </div>
            </div>
          </div>
        );

      case "OTHER_CHOICES":
        return (
          <div className="other-choice-box p-3 border rounded">
            <p className="text-center fw-bold">
              {/* {newContent.label || "Label du choix"} */}
              AUTRE CHOIX
            </p>
            {/* L'ordre est maintenant géré automatiquement */}
          </div>
        );

      case "INPUT":
        return (
          <div className="d-flex flex-column">
            <label className="label-be">
              {newContent.label || "Label de la saisie"}
            </label> 
            <div className="input-wrapper">
              <input 
                type="text" 
                placeholder={newContent.placeholder || "Placeholder"} // Correction du placeholder
                disabled
              />
              {newContent.suffix && <span className="unit">{newContent.suffix}</span>}
            </div>
            {/* L'ordre est maintenant géré automatiquement */}
          </div>
        );
      case "TEXT":
        return (
          <div className="text-content p-3 border rounded">
            {/* <h6>{newContent.name || "Titre du texte"}</h6> */}
            <p>{newContent.description || "Description du texte"}</p>
          </div>
        );

      case "CHOICES":
        return (
          <label className="label-utility">
            <input
              type="radio"
              name="type_batiment"
              defaultValue="autre"
              className="utility-radio"
            />
            <div className="utility mb-3">
              <span>{newContent.label || "Label du choix"}</span>
            </div>
          </label>
        );

      default:
        return <p className="text-muted">Sélectionnez un type pour voir l'aperçu</p>;
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={() => setShowModal(true)}
      >
        <FaPlus /> Ajouter du contenu
      </button>

      {/* Modal pour ajouter du contenu */}
      {showModal && (
        <div className="modal fade show" style={{ display: "block", zIndex: 1050 }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Ajouter du contenu à "{section.title}"</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" style={{color: "#222B40"}}>Type de contenu *</label>
                    <select
                      className="form-control"
                      value={newContent.type}
                      onChange={(e) => setNewContent({...newContent, type: e.target.value})}
                    >
                      <option value="">Sélectionner un type</option>
                      <option value="PRODUCTS">Produits</option>
                      <option value="TEXT">Texte</option>
                      <option value="OTHER_CHOICES">Autre choix</option>
                      <option value="INPUT">Saisie</option>
                      <option value="CHOICES">Choix</option>
                    </select>
                  </div>
                  {/* <div className="col-6 mb-3">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handlePostContent}
                    >
                      Créer
                    </button>
                  </div> */}
                </div>
                <div className="row">
                  <div className="col-md-6">

                    {/* Champs pour choices */}
                    {(newContent.type === "CHOICES" || newContent.type === "OTHER_CHOICES" || newContent.type === "INPUT") && (
                      <div className="mb-3">
                        <label className="form-label">Label *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={newContent.label}
                          onChange={(e) => setNewContent({...newContent, label: e.target.value})}
                          placeholder="Label"
                        />
                      </div>
                    )}

                    {/* Champs pour Product et Text */}
                    {(newContent.type === "PRODUCTS" || newContent.type === "TEXT") && (
                      <>
                        <div className="mb-3">
                          <label className="form-label">
                            {newContent.type === "PRODUCTS" ? "Nom du produit *" : "Titre du texte *"}
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={newContent.name}
                            onChange={(e) => setNewContent({...newContent, name: e.target.value})}
                            placeholder={newContent.type === "PRODUCTS" ? "Nom du produit" : "Titre du texte"}
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Description *</label>
                          <textarea
                            className="form-control"
                            value={newContent.description}
                            onChange={(e) => setNewContent({...newContent, description: e.target.value})}
                            placeholder={newContent.type === "PRODUCTS" ? "Description" : "Contenu du texte"}
                            rows="3"
                          />
                        </div>
                      </>
                    )}

                    {/* Champs spécifiques pour le type INPUT (simple) */}
                    {newContent.type === "INPUT" && (
                      <>
                        <div className="mb-3">
                          <label className="form-label">Placeholder</label>
                          <input
                            type="text"
                            className="form-control"
                            value={newContent.placeholder}
                            onChange={(e) => setNewContent({ ...newContent, placeholder: e.target.value })}
                            placeholder="Placeholder"
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Suffix</label>
                          <input
                            type="text"
                            className="form-control"
                            value={newContent.suffix}
                            onChange={(e) => setNewContent({ ...newContent, suffix: e.target.value })}
                            placeholder="Suffix (ex: mm, m²)"
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Type de saisie</label>
                          <select className="form-control" value={newContent.input_type} onChange={(e) => setNewContent({ ...newContent, input_type: e.target.value })}>
                            <option value="TEXT">Texte</option>
                            <option value="NUMBER">Nombre</option>
                          </select>
                        </div>
                      </>
                    )}

                    {/* Champs spécifiques pour OTHER_CHOICES (multiples) */}
                    {(newContent.type === "INPUT" || newContent.type === "OTHER_CHOICES") &&
                      <div className="mb-3">
                        {newContent.associatedInputs.map((input, index) => (
                        <div key={index} className="border rounded p-3 mb-3 position-relative">
                          {newContent.type === "OTHER_CHOICES" && (
                            <h6 className="text-muted">Champ de saisie #{index + 1}</h6>
                          )}
                          {newContent.type === "OTHER_CHOICES" && newContent.associatedInputs.length > 1 && (
                            <button
                              type="button"
                              className="btn-close position-absolute"
                              style={{ top: '10px', right: '10px' }}
                              onClick={() => removeAssociatedInput(index)}
                            ></button>
                          )}
                          <div className="mb-3">
                            <label className="form-label">Label du champ de saisie *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={input.label}
                              onChange={(e) => handleAssociatedInputChange(index, "label", e.target.value)}
                              placeholder="Label"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Placeholder</label>
                            <input
                              type="text"
                              className="form-control"
                              value={input.placeholder}
                              onChange={(e) => handleAssociatedInputChange(index, "placeholder", e.target.value)}
                              placeholder="Placeholder"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Suffix</label>
                            <input
                              type="text"
                              className="form-control"
                              value={input.suffix}
                              onChange={(e) => handleAssociatedInputChange(index, "suffix", e.target.value)}
                              placeholder="Suffix (ex: mm, m²)"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Type de saisie</label>
                            <select
                              className="form-control"
                              value={input.input_type}
                              onChange={(e) => handleAssociatedInputChange(index, "input_type", e.target.value)}
                            >
                              <option value="TEXT">Texte</option>
                              <option value="NUMBER">Nombre</option>
                            </select>
                          </div>
                        </div>
                      ))}
                    {newContent.type === "OTHER_CHOICES" && newContent.associatedInputs.length < 2 && (
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={addAssociatedInput}>
                        <FaPlus className="me-2" /> Ajouter un deuxième champ de saisie
                      </button>
                    )}
                      </div>}

                    {/* Champs spécifiques pour Product */}
                    {newContent.type === "PRODUCTS" && (
                      <div className="mb-3">
                        <label className="form-label">
                          <FaImage className="me-2" />
                          Image du produit
                        </label>
                        <input
                          type="file"
                          className="form-control"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                        <small className="text-muted">
                          Formats acceptés: JPG, PNG, GIF (max 5MB)
                        </small>
                        
                        {newContent.imagePreview && (
                          <div className="mt-3 text-center">
                            <img 
                              src={newContent.imagePreview} 
                              alt="Aperçu" 
                              className="img-fluid rounded"
                              style={{ maxHeight: '150px', maxWidth: '200px' }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <div className="">
                      <h6>Aperçu du contenu :</h6>
                      <div className="border rounded p-3 bg-light">
                        {renderContentPreview()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Annuler
                </button>
                {!thisContent ? (
                  <button type="button" className="btn btn-primary" onClick={handleCreateContent}>Ajouter</button>
                ) : (
                  <button type="button" className="btn btn-primary" onClick={handleUpdateContent}>Mettre à jour</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Overlay pour le modal */}
      {showModal && (
        <div 
          className="modal-backdrop fade show"
          onClick={() => setShowModal(false)}
          style={{ zIndex: 1040 }}
        ></div>
      )}
    </>
  );
};

export default ContentManager; 